(() => {
  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

  if (!customElements.get('product-modal')) return;

  const ProductModal = customElements.get('product-modal');

  class RenaoProductGallery {
    constructor(modal) {
      this.modal = modal;
      this.content = modal.querySelector('.product-media-modal__content');
      this.current = modal.querySelector('[data-renao-modal-current]');
      this.total = modal.querySelector('[data-renao-modal-total]');
      this.previous = modal.querySelector('[data-renao-modal-previous]');
      this.next = modal.querySelector('[data-renao-modal-next]');
      this.hint = modal.querySelector('[data-renao-modal-hint]');
      this.items = Array.from(this.content?.querySelectorAll(':scope > [data-media-id]') || []);
      this.index = 0;
      this.scale = 1;
      this.x = 0;
      this.y = 0;
      this.pointers = new Map();
      this.startPoint = null;
      this.pinchStart = null;
      this.lastTap = 0;

      if (!this.content || !this.items.length || modal.dataset.renaoGalleryReady === 'true') return;
      modal.dataset.renaoGalleryReady = 'true';
      this.bind();
      this.updateCounter();
    }

    bind() {
      this.previous?.addEventListener('click', () => this.go(-1));
      this.next?.addEventListener('click', () => this.go(1));
      [this.previous, this.next].forEach((button) => {
        button?.addEventListener('pointerup', (event) => event.stopPropagation());
      });
      this.content.addEventListener('pointerdown', (event) => this.onPointerDown(event));
      this.content.addEventListener('pointermove', (event) => this.onPointerMove(event), { passive: false });
      this.content.addEventListener('pointerup', (event) => this.onPointerUp(event));
      this.content.addEventListener('pointercancel', (event) => this.onPointerUp(event));
      this.content.addEventListener('dblclick', (event) => {
        if (this.lastPointerType === 'touch' || this.lastPointerType === 'pen') return;
        event.preventDefault();
        this.toggleZoom(event.clientX, event.clientY);
      });
      this.content.addEventListener('pointerup', (event) => event.stopPropagation());
      this.modal.addEventListener('keydown', (event) => {
        if (!this.modal.hasAttribute('open')) return;
        if (event.key === 'ArrowLeft') this.go(-1);
        if (event.key === 'ArrowRight') this.go(1);
      });
      document.body.addEventListener('modalClosed', () => this.resetZoom());

      const observer = new MutationObserver(() => {
        if (!this.modal.hasAttribute('open')) return;
        this.syncWithActiveMedia();
      });
      observer.observe(this.modal, { attributes: true, attributeFilter: ['open'] });
    }

    syncWithActiveMedia() {
      this.items = Array.from(this.content.querySelectorAll(':scope > [data-media-id]'));
      const active = this.items.findIndex((item) => item.classList.contains('active'));
      this.index = active >= 0 ? active : 0;
      this.activate(this.index, 0);
      this.hint?.classList.remove('is-hidden');
      window.setTimeout(() => this.hint?.classList.add('is-hidden'), 3200);
    }

    activate(index, direction = 0) {
      if (!this.items.length) return;
      this.index = (index + this.items.length) % this.items.length;
      this.resetZoom();

      this.items.forEach((item, itemIndex) => {
        item.classList.toggle('active', itemIndex === this.index);
        item.classList.remove('is-entering-next', 'is-entering-previous');
        item.setAttribute('aria-hidden', itemIndex === this.index ? 'false' : 'true');
      });

      const active = this.items[this.index];
      if (direction > 0) active.classList.add('is-entering-next');
      if (direction < 0) active.classList.add('is-entering-previous');
      active.querySelector('img')?.setAttribute('draggable', 'false');
      this.updateCounter();
      window.pauseAllMedia?.();

      const deferred = active.matches('deferred-media') ? active : active.querySelector('deferred-media');
      if (deferred && deferred.loadContent) deferred.loadContent(false);
    }

    go(direction) {
      if (this.scale > 1.01) {
        this.resetZoom();
        return;
      }
      this.activate(this.index + direction, direction);
    }

    updateCounter() {
      if (this.current) this.current.textContent = String(this.index + 1);
      if (this.total) this.total.textContent = String(this.items.length);
      const hasMultiple = this.items.length > 1;
      if (this.previous) this.previous.hidden = !hasMultiple;
      if (this.next) this.next.hidden = !hasMultiple;
    }

    activeImage() {
      return this.items[this.index]?.matches('img')
        ? this.items[this.index]
        : this.items[this.index]?.querySelector('img');
    }

    onPointerDown(event) {
      const image = this.activeImage();
      if (!image || !event.target.closest('[data-media-id]')) return;
      this.lastPointerType = event.pointerType;
      this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      image.setPointerCapture?.(event.pointerId);

      if (this.pointers.size === 1) {
        this.startPoint = {
          x: event.clientX,
          y: event.clientY,
          translateX: this.x,
          translateY: this.y,
          time: performance.now(),
        };
      } else if (this.pointers.size === 2) {
        const [first, second] = Array.from(this.pointers.values());
        this.pinchStart = {
          distance: Math.hypot(second.x - first.x, second.y - first.y),
          scale: this.scale,
        };
      }
    }

    onPointerMove(event) {
      if (!this.pointers.has(event.pointerId)) return;
      this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

      if (this.pointers.size === 2 && this.pinchStart) {
        event.preventDefault();
        const [first, second] = Array.from(this.pointers.values());
        const distance = Math.hypot(second.x - first.x, second.y - first.y);
        this.scale = clamp(this.pinchStart.scale * (distance / this.pinchStart.distance), 1, 4);
        this.constrainPan();
        this.renderTransform(false);
        return;
      }

      if (this.pointers.size === 1 && this.scale > 1.01 && this.startPoint) {
        event.preventDefault();
        this.x = this.startPoint.translateX + event.clientX - this.startPoint.x;
        this.y = this.startPoint.translateY + event.clientY - this.startPoint.y;
        this.constrainPan();
        this.renderTransform(false);
      }
    }

    onPointerUp(event) {
      if (!this.pointers.has(event.pointerId)) return;
      const point = this.pointers.get(event.pointerId);
      const started = this.startPoint;
      this.pointers.delete(event.pointerId);

      if (this.pointers.size < 2) this.pinchStart = null;
      if (this.scale > 1.01) {
        this.constrainPan();
        this.renderTransform(true);
      } else {
        this.resetZoom();
      }

      if (!started || this.pointers.size) return;
      const deltaX = point.x - started.x;
      const deltaY = point.y - started.y;
      const elapsed = performance.now() - started.time;
      const isSwipe = this.scale === 1 && Math.abs(deltaX) > 52 && Math.abs(deltaX) > Math.abs(deltaY) * 1.15;

      if (isSwipe) {
        this.go(deltaX < 0 ? 1 : -1);
      } else if (
        event.pointerType !== 'mouse' &&
        elapsed < 280 &&
        Math.abs(deltaX) < 12 &&
        Math.abs(deltaY) < 12
      ) {
        const now = performance.now();
        if (now - this.lastTap < 310) {
          this.toggleZoom(event.clientX, event.clientY);
          this.lastTap = 0;
        } else {
          this.lastTap = now;
        }
      }

      this.startPoint = null;
    }

    toggleZoom(clientX, clientY) {
      const image = this.activeImage();
      if (!image) return;
      if (this.scale > 1.01) {
        this.resetZoom();
        return;
      }

      const rect = image.getBoundingClientRect();
      this.scale = 2.5;
      this.x = (rect.left + rect.width / 2 - clientX) * (this.scale - 1);
      this.y = (rect.top + rect.height / 2 - clientY) * (this.scale - 1);
      this.constrainPan();
      this.renderTransform(true);
    }

    constrainPan() {
      const image = this.activeImage();
      if (!image || this.scale <= 1) {
        this.x = 0;
        this.y = 0;
        return;
      }
      const maxX = Math.max(0, (image.clientWidth * (this.scale - 1)) / 2);
      const maxY = Math.max(0, (image.clientHeight * (this.scale - 1)) / 2);
      this.x = clamp(this.x, -maxX, maxX);
      this.y = clamp(this.y, -maxY, maxY);
    }

    renderTransform(animate) {
      const image = this.activeImage();
      if (!image) return;
      image.classList.toggle('is-zoomed', this.scale > 1.01);
      image.classList.toggle('is-transforming', animate);
      image.style.transform = `translate3d(${this.x}px, ${this.y}px, 0) scale(${this.scale})`;
    }

    resetZoom() {
      this.scale = 1;
      this.x = 0;
      this.y = 0;
      const image = this.activeImage();
      if (!image) return;
      image.classList.remove('is-zoomed');
      image.classList.add('is-transforming');
      image.style.transform = '';
    }
  }

  const enhance = () => {
    document.querySelectorAll('product-modal.product-media-modal').forEach((modal) => {
      if (modal instanceof ProductModal) new RenaoProductGallery(modal);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', enhance, { once: true });
  } else {
    enhance();
  }

  document.addEventListener('shopify:section:load', enhance);
})();
